import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useParams, useNavigate } from 'react-router-dom';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import Customization from '../Customization';
import navigation from 'menu-items';
import { drawerWidth } from 'store/constant';
import { SET_MENU, SET_SELECTED_TYPE } from 'store/actions';
import { isEmptyOrNull } from 'utils/validations';

// assets
import { IconChevronRight } from '@tabler/icons';
import { getClientDisplayName } from 'utils/common';
// import { letterSpacing } from '@mui/material/node_modules/@mui/system';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    ...theme.typography.mainContent,
    // Desktop styles - properly account for drawer width
    ...(!open && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        // Desktop: when drawer is closed, take the full width
        [theme.breakpoints.up('md')]: {
            marginLeft: 0,
            marginTop: 0,
            marginRight: 0,
            width: '100%',
            padding: '12px'
        },
        // Mobile: override with mobile-specific values
        [theme.breakpoints.down('md')]: {
            marginLeft: 0,
            width: '100%',
            padding: '16px',
            marginTop: '56px', // Mobile header height
            marginRight: 0
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
            width: '100%',
            padding: '8px',
            marginTop: '56px',
            marginRight: 0
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        // Desktop: when drawer is open, start right after drawer with no gap
        [theme.breakpoints.up('md')]: {
            marginLeft: 0,
            marginTop: 0,
            marginRight: 0,
            width: `calc(100% - ${drawerWidth}px)`, // Take remaining space after drawer
            padding: '12px' // tight, even gutter around the content card
        },
        // Mobile: override with mobile-specific values
        [theme.breakpoints.down('md')]: {
            marginLeft: 0,
            width: '100%',
            padding: '16px',
            marginTop: '56px',
            marginRight: 0
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
            width: '100%',
            padding: '8px',
            marginTop: '56px',
            marginRight: 0
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const params = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // eslint-disable-next-line no-undef
    // const [validUser, setValidUser] = useState(false);
    const matchDownLg = useMediaQuery(theme.breakpoints.down('lg'));
    const [clients, setClients] = useState(null);
    // Handle left drawer
    const leftDrawerOpened = useSelector((state) => state.customization.opened);
    const userData = useSelector((state) => state.user);
    // Handle right sidebar (sideNavBar) visibility for mobile
    const [rightSidebarOpen, setRightSidebarOpen] = useState(false);

    const handleLeftDrawerToggle = () => {
        const newState = !leftDrawerOpened;
        dispatch({ type: SET_MENU, opened: newState });
    };

    const handleRightSidebarToggle = () => {
        setRightSidebarOpen(!rightSidebarOpen);
    };

    // Update body class to control sideNavBar visibility on mobile
    useEffect(() => {
        if (isMobile) {
            if (rightSidebarOpen) {
                document.body.classList.add('right-sidebar-open');
            } else {
                document.body.classList.remove('right-sidebar-open');
            }
        }
        return () => {
            document.body.classList.remove('right-sidebar-open');
        };
    }, [rightSidebarOpen, isMobile]);
    // #todo , check client is authorized

    useEffect(() => {
        if (isEmptyOrNull(userData)) {
            const returnTo = `${window.location.pathname}${window.location.search}`;
            window.location.href = `/login?ru=${encodeURIComponent(returnTo)}`;
        }
        dispatch({ type: SET_SELECTED_TYPE, payload: params.client });
    }, []);

    useEffect(() => {
        let listOfClients = null;
        if (userData && userData.clients) {
            const { clients } = userData;
            listOfClients = Object.keys(clients)
                .reduce((acc, key) => {
                    const clientObj = clients[key];
                    if (userData.admin || clientObj.admin || (clientObj.details.j_id && clientObj.directAccess)) {
                        acc.push({
                            value: key,
                            displayName: getClientDisplayName(key, clients) // #todo , need to get the userDetails as well in case resolve_ref = true
                        });
                    }
                    return acc;
                }, [])
                .sort((a, b) => String(a.displayName).localeCompare(String(b.displayName), undefined, { sensitivity: 'base' }));
            setClients(listOfClients);
        }
    }, [userData]);

    // Set initial sidebar state based on screen size (only on mount)
    useEffect(() => {
        // On mobile (lg and below), sidebar should be closed by default
        // On desktop (lg and above), sidebar should be open by default
        const isMobile = window.innerWidth < 1200; // lg breakpoint is 1200px
        dispatch({ type: SET_MENU, opened: !isMobile });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line no-debugger

    const dropDownData = {
        list: clients,
        selectedValue: params.client
    };

    // #todo :  use to fetch the Data for navigation from jeanie server here and
    // pass that to Sidebar

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* <div> production :: {process.env.NODE_ENV}</div>
            <div> it should be come here {process.env.REACT_APP_IS_STAGING}</div> */}
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: theme.palette.background.default,
                    transition: leftDrawerOpened ? theme.transitions.create('width') : 'none',
                    // Ensure AppBar is visible on both mobile and desktop
                    display: 'flex',
                    zIndex: (theme) => theme.zIndex.drawer + 1
                }}
            >
                {isMobile && (
                    <Toolbar
                        sx={{
                            // Ensure Toolbar is visible on mobile
                            display: 'flex',
                            minHeight: { xs: '56px', md: '64px' },
                            padding: { xs: '0 8px', md: '0 16px' }
                        }}
                    >
                        <Header
                            handleLeftDrawerToggle={handleLeftDrawerToggle}
                            dropDown={dropDownData}
                            rightSidebarOpen={rightSidebarOpen}
                            handleRightSidebarToggle={handleRightSidebarToggle}
                        />
                    </Toolbar>
                )}
            </AppBar>

            {/* drawer */}
            {/* will get data for rendering cleints and types once user is validated and we selected the client */}
            <Sidebar drawerOpen={leftDrawerOpened} drawerToggle={handleLeftDrawerToggle} dropDown={dropDownData} />

            {/* main content */}
            <Main theme={theme} open={leftDrawerOpened}>
                <Box
                    sx={{
                        p: { xs: '12px', md: '16px' },
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        height: { xs: 'calc(100vh - 72px)', md: 'calc(100vh - 24px)' },
                        width: '100%',
                        overflow: 'auto'
                    }}
                >
                    {/* breadcrumb */}
                    <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                    <Outlet />
                </Box>
            </Main>
            {/* <Customization /> */}
        </Box>
    );
};

export default MainLayout;
